import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import type { EntityDefinition } from "@/shared/contracts/structure";
import { request } from "@/shared/http/client";

/**
 * O CAMPO OBRIGATÓRIO QUE A CONVERSA NÃO TEM — nomeado, e com o conserto a um clique.
 *
 * ── DE ONDE VEM A LACUNA ──────────────────────────────────────────────────
 *
 * `PUT /v3/automation/channel-funnels/:type` recusa com
 * `400 { error: "MISSING_REQUIRED", fields: string[] }` quando o funil escolhido exige,
 * em todo registro, um dado que um card nascido de conversa não tem
 * (`put-contact-on-funnel.ts: birthGaps`). A recusa está certa e chega na hora certa —
 * o que faltava era a saída: a frase mandava a pessoa abrir a tela do funil, consertar
 * lá e voltar.
 *
 * `PATCH /v3/structure/entities/:code/fields/:field { required: false }` já está no ar
 * e tirar o `NOT NULL` NUNCA quebra linha existente — `rows_would_break` só acontece no
 * sentido contrário. Logo o conserto cabe num botão, na própria caixa que recusou.
 *
 * ── POR QUE O CÓDIGO DO CAMPO NÃO BASTA ───────────────────────────────────
 *
 * O servidor manda CÓDIGOS (`canal`, `cpf`), e um código não é o que a pessoa vê na
 * tela do funil — ela vê o RÓTULO. E as lacunas podem vir de DUAS entidades: a do
 * funil, e a ficha-pessoa quando o funil alcança a pessoa por um salto (`ref_entity`
 * → `ref_contact`). O botão precisa saber em qual escrever, e a frase precisa dizer de
 * quem é o campo, senão "precisa de CPF" aparece sobre um funil que não tem CPF nenhum.
 *
 * Então a resolução acontece aqui, contra `GET /v3/structure/entities` — a lista
 * inteira, que a conta já lê noutras telas e que fica em cache. Um salto, que é
 * exatamente o alcance que o servidor tem.
 *
 * ── POR QUE ISTO MORA EM `shared/` E NÃO EM `features/channels/api` ───────
 *
 * Porque a recusa não é da tela de canais. A MESMA rota é chamada de dois lugares —
 * o modal que liga um canal a um funil e o painel "por onde entra gente" dentro do
 * próprio funil (`features/board`) —, e `no-cross-feature` (severidade `error`)
 * impede o segundo de importar o primeiro. Nasceu em `features/channels` porque foi
 * lá que a recusa apareceu primeiro; enquanto ficou lá, o quadro mostrava a frase
 * CRUA do servidor, com o código do campo no lugar do rótulo.
 *
 * O `request` daqui é a exceção que `shared/access`, `shared/workspace` e
 * `shared/agent-display` já são: uma leitura que não é de feature nenhuma fala HTTP
 * do lugar onde ela é de todos. O que a regra proíbe é um COMPONENTE falar HTTP —
 * e nenhum fala: `MissingRequiredNotice.vue` ao lado pede tudo a este arquivo.
 */

/**
 * A recusa lida do ERRO, e não da mensagem — os códigos, como o servidor os manda.
 *
 * `channel-funnel.controller.ts` manda `fields` como DADO justamente para a tela poder
 * montar a frase dela. Ler por `payload` e não por `instanceof HttpError` é de
 * propósito: os specs trocam o módulo do cliente por um dublê, e um `instanceof` contra
 * a classe verdadeira devolveria `null` em todos eles — o teste ficaria verde sobre uma
 * caixa que nunca desenha.
 *
 * `null` quer dizer "esta falha não é esta" — quem chama continua a mostrar a frase do
 * servidor, que é o certo para um 409 ou um 403.
 */
export function missingRequiredFields(error: unknown): readonly string[] | null {
	const payload = (error as { payload?: { error?: string; fields?: unknown } } | null)?.payload;
	if (payload?.error !== "MISSING_REQUIRED" || !Array.isArray(payload.fields)) return null;
	// O contrato de hoje é `string[]`; um servidor que passe a mandar
	// `{ entity, code, label }` continua sendo lido pelo `code`.
	const codes = payload.fields
		.map((f) => (typeof f === "string" ? f : (f as { code?: string })?.code))
		.filter((code): code is string => typeof code === "string");
	// Uma recusa sem campo nenhum não tem caixa para desenhar: seria um aviso a
	// prometer um botão que não teria o que consertar.
	return codes.length > 0 ? codes : null;
}

/** Uma lacuna já legível: onde ela mora, como se chama no banco e na tela. */
export interface FieldGap {
	/** A entidade a corrigir — nem sempre a do funil. */
	readonly entity: string;
	/** O nome dela na tela, para a frase poder dizer de quem é o campo. */
	readonly entityName: string;
	readonly code: string;
	readonly label: string;
	/** Falso quando a lacuna veio da ficha-pessoa, e não do próprio funil. */
	readonly onFunnel: boolean;
}

/**
 * A MESMA chave e a MESMA forma com que `features/contacts` e `features/inbox` já leem
 * esta lista: uma resposta em cache para o app inteiro, sem uma feature importar outra.
 * Uma chave própria daqui seria um segundo pedido do mesmo catálogo por tela.
 */
const ENTITIES = ["structure", "entities"] as const;

export function useEntities() {
	return useQuery({
		queryKey: ENTITIES,
		queryFn: async () => {
			const res = await request<{ entities: EntityDefinition[] }>("/v3/structure/entities");
			return res.entities;
		},
		staleTime: 60_000,
	});
}

/**
 * Os códigos recusados, virados em campos com nome e dono.
 *
 * Um código que não se acha em lugar nenhum volta como ele mesmo, com `label` igual ao
 * código e a entidade do funil: dizer o código cru é feio, e esconder a lacuna é pior —
 * a pessoa ficaria com uma recusa sem frase.
 */
export function useFieldGaps(pipelineEntity: () => string | null, codes: () => readonly string[]) {
	const { data: entities } = useEntities();

	function resolve(): FieldGap[] {
		const all = entities.value ?? [];
		const entity = all.find((e) => e.code === pipelineEntity());
		// Um salto: as entidades que os `ref_entity` deste funil alcançam — a mesma
		// conta que `personPath` faz no servidor, e nada além dela.
		const hops = (entity?.fields ?? [])
			.filter((f) => f.type === "ref_entity" && typeof f.config?.entity === "string")
			.map((f) => all.find((e) => e.code === f.config.entity))
			.filter((e): e is EntityDefinition => Boolean(e));

		return codes().map((code) => {
			const own = entity?.fields.find((f) => f.code === code && !f.archived);
			if (own && entity) {
				return {
					entity: entity.code,
					entityName: entity.name,
					code,
					label: own.label,
					onFunnel: true,
				};
			}
			for (const hop of hops) {
				const found = hop.fields.find((f) => f.code === code && !f.archived);
				if (found) {
					return {
						entity: hop.code,
						entityName: hop.name,
						code,
						label: found.label,
						onFunnel: false,
					};
				}
			}
			return {
				entity: entity?.code ?? "",
				entityName: entity?.name ?? "",
				code,
				label: code,
				onFunnel: true,
			};
		});
	}

	return { resolve, ready: () => (entities.value?.length ?? 0) > 0 };
}

/**
 * Tira o obrigatório de um campo.
 *
 * `@MinRole('admin')` no servidor — a tela só oferece o botão a quem pode, e a quem não
 * pode diz a quem pedir. Invalida a estrutura E o quadro: quem lê a mesma entidade
 * noutra aba tem de parar de exigir o campo no mesmo tique.
 */
export function useMakeFieldOptional() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (input: { entity: string; field: string }) =>
			request(
				`/v3/structure/entities/${encodeURIComponent(input.entity)}/fields/${encodeURIComponent(input.field)}`,
				{ method: "PATCH", body: { required: false } },
			),
		onSuccess: () => {
			void client.invalidateQueries({ queryKey: ENTITIES });
			// O quadro desenha os cartões desta mesma entidade: um campo que deixou de
			// ser obrigatório tem de parar de o ser lá no mesmo tique.
			void client.invalidateQueries({ queryKey: ["board"] });
		},
	});
}
